import { useState, useEffect } from 'react'
import axios from 'axios'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { PlatinumData } from '../types/carbon/models/PlatinumData'
import { TableVisibilitySettings, TableTimingsResponse, TableLoadTiming, TableDataResponse } from '../types/app'
import {
  API_BASE_URL,
} from '../constants/system'
import useAccount from './useAccount'
import useSnackbar from './useSnackbar'
import { extractErrorMessage } from '../utils/apitools'
import { withAndedFilter } from '../utils/filter'
import { useSharedDashboardId } from '../contexts/SharedDashboardContext'
import { useDashboardGrantId } from '../contexts/DashboardGrantContext'

export const useTableData = ({
  customer,
  job,
  table,
  filter,
  visibility = {},
  useCache = true,
  spec,
}: {
  customer: string,
  job: string,
  table: string,
  filter: string,
  visibility?: Partial<TableVisibilitySettings>,
  useCache?: boolean,
  spec?: Record<string, unknown>,
}) => {
  const account = useAccount()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()
  const sharedDashboardId = useSharedDashboardId()
  const isSharedMode = Boolean(sharedDashboardId)
  // Inside a dashboard, the table loads carry its id: the dashboard grant
  // (DashboardGrantContext) lets a viewer read a table the email domain list
  // would otherwise hide.
  const grantDashboardId = useDashboardGrantId()
  const grantParams = grantDashboardId && !isSharedMode ? { dashboard: grantDashboardId } : {}
  const [enforcedLoading, setEnforcedLoading] = useState(true)
  const [cacheBuster, setCacheBuster] = useState(0)
  const [tableTiming, setTableTiming] = useState<TableLoadTiming>({ started: null, estimated: null, isCached: false, cachedAt: null, lastRunDuration: null })

  const isSpecMode = Boolean(spec) && !table

  const dataUrl = isSpecMode
    ? (isSharedMode
        ? `${API_BASE_URL}/shared/dashboard/${sharedDashboardId}/tables/${job}/run`
        : `${API_BASE_URL}/tables/${customer}/${job}/run`)
    : (isSharedMode
        ? `${API_BASE_URL}/shared/dashboard/${sharedDashboardId}/tables/${job}/data`
        : `${API_BASE_URL}/tables/${customer}/${job}/data`)

  const queryKey = ['table', {
    customer,
    job,
    table,
    filter,
    ...visibility,
    cacheBuster,
    useCache,
    sharedDashboardId,
    spec,
  }]

  const loadTableData = useQuery({
    queryKey,
    enabled: Boolean(customer && job && (account.user || isSharedMode)),
    // When cache bypass is active, don't let React Query cache responses either
    staleTime: useCache ? undefined : 0,
    gcTime: useCache ? undefined : 0,
    queryFn: async () => {
      if(!customer || !job || (!account.user && !isSharedMode)) return null

      // Spec mode: POST the spec to the /run endpoint
      if (isSpecMode && spec) {
        try {
          // AND the dashboard filter onto the spec's own, bracketed so an OR
          // in either cannot change what the other means.
          const mergedSpec = withAndedFilter(spec as typeof spec & { filter?: string; useFilter?: boolean }, filter)

          const dataRes = await axios.post<PlatinumData>(dataUrl, mergedSpec, {
            params: {
              format: 'json',
              frequency: 'false',
              column_percent: 'true',
            }
          })

          return {
            tableData: dataRes.data,
          } as {
            tableData: PlatinumData
          }
        } catch (error) {
          throw new Error(extractErrorMessage(error))
        }
      }

      // Path mode: existing GET logic
      if (!table) return null
      if(table === 'new' || table === 'new.cbt') return null

      try {
        if (!isSharedMode) {
          // First, fetch timing estimates to show progress (authenticated mode only)
          const timingRes = await axios.get<TableTimingsResponse>(`${API_BASE_URL}/tables/${customer}/${job}/load-timings`, {
            params: {
              path: table,
              filter,
              ...visibility,
              ...grantParams,
            }
          })

          // Set timing state with load estimate, cache status, and run duration
          const loadDuration = timingRes.data.load_duration_ms ?? timingRes.data.duration_ms
          const runDuration = timingRes.data.run_duration_ms
          const isCached = timingRes.data.is_cached ?? false

          // Set initial timing state from load-timings endpoint
          setTableTiming({
            started: (loadDuration && loadDuration > 0 && !isCached) ? Date.now() : null,
            estimated: loadDuration,
            isCached: isCached,
            cachedAt: null,
            lastRunDuration: runDuration,
          })
        }

        // Request wrapped response with cache info
        // When useCache is false (manager bypass), send nocache=true
        const dataRes = await axios.get<TableDataResponse>(dataUrl, {
          params: {
            path: table,
            filter,
            nocache: useCache ? '' : 'true',
            include_cache_info: 'true',
            ...visibility,
            ...grantParams,
          }
        })

        // Update timing with cache info from the data response (authoritative source)
        const cacheInfo = dataRes.data.cache_info
        setTableTiming(prev => ({
          ...prev,
          isCached: cacheInfo.is_cached,
          cachedAt: cacheInfo.cached_at,
        }))

        return {
          tableData: dataRes.data.data,  // Unwrap from response
        } as {
          tableData: PlatinumData
        }
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  const data = loadTableData.data
  const isLoading = loadTableData.isLoading
  const isError = loadTableData.isError
  const error = loadTableData.error

  // Create dashboard mutation
  const deleteTable = async (customer: string, job: string, path: string) => {
    if (!customer || !job) {
      throw new Error('Customer is required')
    }

    await axios.delete(`${API_BASE_URL}/tables/${customer}/${job}/data?path=${encodeURIComponent(path)}`)
    snackbar.success('Table deleted')
  }

  const setTempLoading = () => {
    setEnforcedLoading(true)
    setTimeout(() => {
      setEnforcedLoading(false)
    }, 100)
  }

  useEffect(() => {
    setEnforcedLoading(true)
    const timer = setTimeout(() => {
      setEnforcedLoading(false)
    }, 100)
    return () => clearTimeout(timer)
  }, [JSON.stringify(queryKey)])

  // Force reload table data by deleting cache record and refetching
  const reloadTable = async () => {
    if (!customer || !job || !table) return

    // 1. Delete ALL cache records for this table path (ignores visibility variants)
    await axios.delete(`${API_BASE_URL}/tables/${customer}/${job}/cache`, {
      params: { path: table }
    })

    // 2. Remove the cached data from React Query (this shows the loading spinner)
    queryClient.removeQueries({ queryKey })

    // 3. Trigger a fresh load via cacheBuster
    setCacheBuster(existing => existing + 1)
  }

  return {
    loading: isLoading || enforcedLoading,
    setTempLoading,
    data: data?.tableData,
    isError,
    error,
    refetch: loadTableData.refetch,
    deleteTable,
    tableTiming,
    reloadTable,
  }
}

export default useTableData
